import {
	CSSObject,
	Container,
	Grid,
	Modal,
	Select,
	Space,
} from "@mantine/core";
import type { BaseSelectStylesNames } from "@mantine/core/lib/components/Select/types";
import type { AxiosError } from "axios";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { ErrorResponse } from "src/lib/types";
import styled from "styled-components";
import mint_image from "../../assets/images/mint.svg";
import { useTokenDetails } from "../../context/token-details-context";
import { cacheKey } from "../../hooks/cacheStateKey";
import { useBurnTokens } from "../../hooks/useWallets";
import { AnimatedLabelInput } from "../AnimatedLabelInput";
import { PrimaryButton, SecondaryButton } from "../Buttons";
import { TextInput } from "../Inputs";
import { ErrorText } from "../LoginForm";
import { SuccessModal } from "./SuccessModal";

const FormSection = styled.div`
  margin-top: 25px;
`;

interface Iprops {
	setIsVisible: React.Dispatch<React.SetStateAction<boolean>>;
	isVisible: boolean;
	callback?: () => void;
}

export const BurnCoinsForm = ({
	isVisible,
	setIsVisible,
	callback,
}: Iprops) => {
	const { mutate: burnTokens, isLoading } = useBurnTokens();
	const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
	const [amount, setAmount] = useState<number>(0);
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	const { walletSummaryDetails, tokenDetails } = useTokenDetails();
	const wallets = walletSummaryDetails?.wallets || [];
	const tokenId = tokenDetails?.[0]?.id;

	const {
		control,
		handleSubmit,
		formState: { errors },
	} = useForm({
		mode: "all",
	});

	const masterReserveWallet = wallets?.find(
		(wallet) => wallet?.category === "Master",
	);

	interface IFormData {
		amount: string;
	}

	const burn = (data: IFormData) => {
		burnTokens(
			{
				amount: Number(data.amount),
				tokenId,
			},
			{
				onSuccess: () => {
					queryClient.invalidateQueries(cacheKey.walletBalanceAndTokenDetails);
					queryClient.invalidateQueries(cacheKey.tokenReportSummary);
					queryClient.invalidateQueries(cacheKey.tokens);
					queryClient.invalidateQueries(cacheKey.walletSummary);
					setAmount(() => Number(data.amount));
					setShowSuccessModal(true);
					// setIsVisible(false);
					callback?.();
				},
				onError: (error: AxiosError<ErrorResponse>) => {
					if (error?.response?.data?.message || error?.message) {
						toast.error(error?.response?.data?.message || error.message);
					}
				},
			},
		);
	};
	return (
		<Modal
			size="900px"
			opened={isVisible}
			centered
			onClose={() => setIsVisible(false)}
		>
			<Screen fluid>
				<PageContainer>
					<InnerWrapper>
						<ImageWrapper>
							<PageImage src={mint_image} alt="Manual Distribution Image" />
						</ImageWrapper>
						<form onSubmit={handleSubmit(burn)}>
							<FormWrapper>
								<FormHeader>{t("burn.tokens")}</FormHeader>
								<FormSection>
									<Select
										label={t("choose.wallet.destination")}
										value={masterReserveWallet?.id}
										styles={selectStyles}
										data={[masterReserveWallet]?.map((item) => ({
											label: `${item?.category} Wallet`,
											value: item?.id,
										}))}
									/>
								</FormSection>
								<FormSection>
									<TextInput>
										<Controller
											name="amount"
											control={control}
											rules={{
												required: t("transfer.amount.required"),
												pattern: {
													value: /^[0-9]\d*$/,
													message: t("transfer.amount.invalid"),
												},
												validate: (value) =>
													Number(value) > 0 || t("transfer.amount.invalid"),
											}}
											render={({ field: { ref, onBlur, onChange, value } }) => (
												<AnimatedLabelInput
													label={t("amount")}
													name="amount"
													type="number"
													value={value}
													onChange={onChange}
													onBlur={onBlur}
													ref={ref}
													isLabelAnimated={false}
												/>
											)}
										/>
									</TextInput>
									{errors?.amount?.message && (
										<ErrorText>{errors.amount.message}</ErrorText>
									)}
								</FormSection>
								<Space h={24} />

								<ButtonArea>
									<PrimaryButton
										style={{ height: 40 }}
										title={t("burn.title")}
										loading={isLoading}
									/>
									<SecondaryButton
										title={t("close.button.label")}
										style={{ width: 152 }}
										onClick={() => setIsVisible(false)}
									/>
								</ButtonArea>
							</FormWrapper>
						</form>
					</InnerWrapper>
				</PageContainer>
			</Screen>
			<SuccessModal
				isVisible={showSuccessModal}
				title={t("burning.success.title")}
				message={`${t("burning.success.description")}\n`}
				amount={amount}
				buttonText={t("close.button.label")}
				onClose={() => {
					setIsVisible(false);
					setShowSuccessModal(false);
				}}
			/>
		</Modal>
	);
};

const selectStyles: Partial<Record<BaseSelectStylesNames, CSSObject>> = {
	input: {
		border: "none",
		borderBottom: "1px solid #E3E2E2",
		borderRadius: 0,
		paddingLeft: 0,
		fontFamily: "'ProximaNova', sans-serif;",
		color: "var(--textColor)",
		backgroundColor: "transparent",
	},
	label: {
		fontSize: 12,
		fontFamily: "'ProximaNova', sans-serif;",
		color: "#828282",
	},
	rightSection: { pointerEvents: "none", display: "none" },
	root: {
    
  },
	selected: {
		backgroundColor:"var(--accentBg)",
    border: "none",
    color: "var(--textColor)",
	},
	dropdown: {
		backgroundColor: "var(--accentBg)",
		border: "none",
	},
  item: {
		color: 'var(--textColor)',
    '&:hover': {
      color: '#828282',
      backgroundColor: "",
    }
	}
}

const Screen = styled(Container)`
  padding: 0;
  a {
    font-size: 13px;
    font-weight: bold;
    color: ${({ theme }) => theme.colors.primary.black};
  }
`;

const PageContainer = styled.div`
  margin-top: -80px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 130px 0;
`;

const InnerWrapper = styled(Grid)`
  justify-content: center;
  display: flex;
`;

const ImageWrapper = styled.div`
  margin-right: 100px;
`;

const PageImage = styled.img`
  max-width: 269px;
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 400px;
  min-width: 390px;
`;

const ButtonArea = styled.div`
  display: flex;
  justify-content: space-between;
`;

const FormHeader = styled.h1`
  font-size: 24px;
  margin: 0;
  font-family: 'ProximaNovaExtraBold', sans-serif;
  color: var(--textColor);
`;
